import type {
  PlaylistSession,
  PlaylistSessionControls,
} from "./usePlaylistSession";
import { formatPlayhead } from "./useVideoPlayhead";
import { FeedbackMediaThumbnail } from "@/features/coach-review/components/FeedbackMediaThumbnail";

/**
 * Playlist session rail — the control strip under the focused
 * feedback player's video while a playlist session is running.
 *
 * Ports the legacy `renderPlaylistSessionRail`
 * (`js/coaching/feedback-player.js:807-844`): the current item's
 * thumbnail + "Review Session" label + playlist title +
 * `{index} of {total} · {item title} · {category}` + a Prev /
 * Pause-Resume / Restart / Next control row.
 *
 * Presentational — all session state + the advance monitor live
 * in `usePlaylistSession`. This component just renders the
 * `session` snapshot and wires the buttons to `controls`.
 *
 * **Privacy:** never templates `coach_private_note` (the server
 * scrubs it for viewers; the rail never references it).
 */

export type PlaylistSessionRailProps = {
  session: PlaylistSession;
  controls: PlaylistSessionControls;
};

export function PlaylistSessionRail({
  session,
  controls,
}: PlaylistSessionRailProps) {
  const { currentItem, index, total, paused, playlist } = session;

  return (
    <div
      className="flex items-center gap-3 rounded border border-rule bg-surface p-2 shadow-sm"
      data-testid="playlist-session-rail"
    >
      {/* Current item thumbnail — the auth-Bearer blob fetch; the
       * film-strip placeholder shows until it resolves / on
       * failure. */}
      <FeedbackMediaThumbnail
        kind="note"
        id={currentItem.id}
        testId="playlist-session-thumb"
        placeholderTestId="playlist-session-thumb-placeholder"
        className="h-12 w-20 shrink-0 overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface"
      />

      <div className="min-w-0 flex-1">
        <span className="text-ink-muted text-[0.65rem] font-display uppercase tracking-wider">
          Review Session
        </span>
        <div className="text-ink text-sm font-display truncate">
          {playlist.title}
        </div>
        <div
          className="text-ink-muted text-xs truncate"
          data-testid="playlist-session-position"
        >
          {index + 1} of {total} · {currentItem.title} ·{" "}
          {currentItem.category || "note"} ·{" "}
          <span className="font-mono tabular-nums">
            {formatPlayhead(Number(currentItem.timestamp_seconds ?? 0))}
          </span>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <RailButton
          label="Prev"
          testid="playlist-session-prev"
          onClick={controls.prev}
          disabled={index === 0}
        />
        <RailButton
          label={paused ? "Resume" : "Pause"}
          testid="playlist-session-pause"
          onClick={controls.togglePause}
        />
        <RailButton
          label="Restart"
          testid="playlist-session-restart"
          onClick={controls.restart}
        />
        <RailButton
          label="Next"
          testid="playlist-session-next"
          onClick={controls.next}
        />
      </div>
    </div>
  );
}

function RailButton(props: {
  label: string;
  testid: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      data-testid={props.testid}
      className="rounded border border-rule bg-canvas px-2 py-1 text-xs font-mono text-ink transition-colors hover:bg-field disabled:opacity-40 disabled:hover:bg-canvas"
    >
      {props.label}
    </button>
  );
}
